import {
  Component,
  OnInit,
  OnDestroy,
  Injector,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';

import { BasicFormComponent } from '@tuain/ng-forms';

import { FormOperationService } from '@services/form-manager.service';
import { EventManagerService } from '@services/event-manager.service';
import { FileManagementService } from '@services/file-management.service';

import { AppConfigService } from '@services/app-configuration.service';
import { APIGatewayService } from '@services/api-gateway.service';
import { GeocodeService } from '@services/geocode.service';
import { AppLoggerService } from '@services/app-logger.service';
import { UserSessionService } from '@services/user-session.service';
import { LiveConnectionService } from '@services/live-connection.service';
import { ComponentPageService } from '@services/component-pages.service';
import { StorageService } from '@services/storage.service';

import { formConfig } from '@components/tuain.module.config';

import { defaultConfig } from 'src/app/app.config';
import { FormModalsHost, ModalOption, SectionModal, SimpleModal } from '@components/modals/form-modals.types';

const { configLoaded, sessionToBeClosed } = defaultConfig.appEvents;

const DEFAULT_MODAL_WIDTH = '70%';
const DEFAULT_MODAL_MIN_HEIGHT = '500px';

export { ModalOption, SectionModal, SimpleModal } from '@components/modals/form-modals.types';

@Component({
  standalone: true,
  selector: 'app-base-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class AppBaseFormComponent extends BasicFormComponent implements OnInit, OnDestroy, FormModalsHost {
  initialStates: string[] = [];
  debug = false;
  assetsBase: string;
  visibleHeaderActions: any[] = [];
  mobile = false;
  showMenu = false;
  tabType = 'line';
  tabPosition = 'top';
  layout: string;
  eventSubscriptions: any[] = [];
  currentAlert: any = null;
  currentError: any = null;
  _showServerErrorPopup = true;
  _blockingProcessing = false;
  // Compatibilidad móvil >>>>>>
  headerBadgeCounter = 0;
  statusBarHeight = 36;
  showRefresher = false;
  pageClass = '';
  description = '';
  iconName = '';
  // Compatibilidad móvil <<<<<<
  sectionModal: SectionModal = {
    visible: false,
    title: '',
    code: '',
    actions: null,
    callback: null,
    properties: {
      width: DEFAULT_MODAL_WIDTH,
      minHeight: DEFAULT_MODAL_MIN_HEIGHT,
      maskClosable: true,
    },
  };

  simpleModal: SimpleModal = {
    visible: false,
    title: '',
    type: 'text',
    content: null,
    template: null,
    options: null,
    callback: null,
    properties: {
      width: DEFAULT_MODAL_WIDTH,
      minHeight: DEFAULT_MODAL_MIN_HEIGHT,
    },
  };

  helpDialog: SimpleModal = {
    visible: false,
    title: '',
    type: 'text',
    content: null,
    template: null,
    options: null,
    callback: null,
    properties: {
      width: DEFAULT_MODAL_WIDTH,
      minHeight: DEFAULT_MODAL_MIN_HEIGHT,
    },
  };

  // form: FormGroup;
  headerBackground = '1';
  modalDialogs: any = {};

  protected _appConfig: AppConfigService;
  protected _apiGateway: APIGatewayService;
  protected _route: ActivatedRoute;
  protected _router: Router;
  protected logger: AppLoggerService;
  protected _userSession: UserSessionService;
  protected _storageService: StorageService;
  protected _liveConnection: LiveConnectionService;
  protected _componentPageService: ComponentPageService;
  protected _geocode: GeocodeService;
  private readonly _cdr: ChangeDetectorRef;

  constructor() {
    const injector = inject(Injector);

    super(injector.get(FormOperationService), injector.get(EventManagerService), injector.get(FileManagementService));
    this.setConfig(formConfig as any);
    this._route = injector.get(ActivatedRoute);
    this._router = injector.get(Router);
    this._appConfig = injector.get(AppConfigService);
    this._apiGateway = injector.get(APIGatewayService);
    this.logger = injector.get(AppLoggerService);
    this._userSession = injector.get(UserSessionService);
    this._storageService = injector.get(StorageService);
    this._liveConnection = injector.get(LiveConnectionService);
    this._componentPageService = injector.get(ComponentPageService);
    this._geocode = injector.get(GeocodeService);
    this._cdr = injector.get(ChangeDetectorRef);
    // Flujo personalizado
    this.layout = (this.formConfig as any).defaultFieldsLayout;
    this.debug = this._appConfig.getParameter('debug') ?? false;
    this.mobile = this._appConfig.getParameter('mobile') ?? false;
  }

  override start() {
    if (this.mobile) {
      this.initialStates = this.states?.[0] ? [this.states?.[0]] : [];
    } else {
      this.initialStates = this.states ?? [];
    }
    this.subscribeAppEvent(configLoaded.name, () => (this.assetsBase = this._appConfig.getParameter('assetsBase')));
    this.onFormChange(() => this.customizeHeaderActions());
    this.customizeHeaderActions();
    this.iconName = this.resolveHeaderIcon();
  }

  /**
   * Resuelve el icono del header desde el menú (appMenus). Si el form no es un
   * item del menú (drill-down), hereda el icono del form de origen subiendo la
   * cadena de navegación. Sin coincidencia => sin icono.
   */
  private resolveHeaderIcon(): string {
    const directIcon = this._userSession.getMenuIconForForm(this.name);
    if (directIcon) {
      return directIcon;
    }
    const visited = new Set<string>();
    let origin = this.getOriginDetail();
    while (origin?.name && !visited.has(origin.name)) {
      visited.add(origin.name);
      const originIcon = this._userSession.getMenuIconForForm(origin.name);
      if (originIcon) {
        return originIcon;
      }
      origin = origin.originToken ? this.formManagerService.getFormInfo(origin.originToken) : null;
    }
    return '';
  }

  get elements() {
    const allElements = [];
    const allSections = this.getSections();
    for (const section of allSections) {
      const subsections = section.subSections;
      for (const subsection of subsections) {
        for (const element of subsection.subSectionElements) {
          allElements.push(element);
        }
      }
    }
    return allElements;
  }

  enableActionUponFields(fields: string[] | string, actions: string[] | string, options: any = {}): boolean {
    const { disableIfNotChanged = false, onlyVisible = false, checkCompleteness = false } = options;
    const fieldCodes = Array.isArray(fields) ? fields : [fields];
    const actionCodes = Array.isArray(actions) ? actions : [actions];
    const fieldsWithError = this.getFieldsWithValidationIssues(fieldCodes, null, null, onlyVisible);
    const emptyFields = this.getRequiredEmptyFields(fieldCodes, null, null, onlyVisible);
    let incompleteFields = [];
    if (checkCompleteness) {
      incompleteFields = this.getFieldSet(
        fld => (!onlyVisible || fld?.visible) && fld.minLength && fld.value?.length < fld.minLength,
        fieldCodes,
        null,
        null
      );
    }
    let canEnable = !(fieldsWithError?.length > 0 || emptyFields?.length > 0 || incompleteFields?.length > 0);
    if (disableIfNotChanged) {
      const changedFields = this.getChangedFields(fieldCodes);
      canEnable &&= changedFields.length > 0;
    }
    actionCodes.forEach(action => {
      if (canEnable) {
        this.enableAction(action);
      } else {
        this.disableAction(action);
      }
    });
    return canEnable;
  }

  customizeHeaderActions() {
    this.visibleHeaderActions = this.getHeaderActions().filter?.(item => item?.visible) ?? [];
  }

  log(message) {
    this.logger.log(message);
  }

  override goBackForm(forced = false): void {
    if (forced || this.form?.initialStates?.includes(this.state)) {
      super.goBackForm();
    }
  }

  /**
   * Pide a Angular que revise esta vista en el próximo ciclo.
   *
   * Las páginas de formulario corren con `ChangeDetectionStrategy.OnPush`, que es el valor por
   * omisión desde Angular 22. Sus plantillas leen campos planos —`visible`, `form`,
   * `enabledSections`, `currentError`— que cambian desde callbacks: eventos de aplicación,
   * parámetros de ruta, transiciones de estado del formulario, acciones y validaciones de campo.
   * Ninguna de esas vías pasa por un binding ni por una señal, así que sin avisar el resultado
   * sería una pantalla congelada, y es un fallo que ninguna prueba unitaria detecta.
   *
   * Se marca en los embudos y no en cada asignación: por aquí entra todo lo que puede cambiar la
   * vista de un formulario.
   */
  protected marcarParaRevision(): void {
    this._cdr.markForCheck();
  }

  subscribeAppEvents(eventClass, callback) {
    this.eventSubscriptions.push(
      this._eventEmiter.subscribe(eventClass, eventData => {
        callback(eventData);
        this.marcarParaRevision();
      })
    );
  }

  emitAppEvents(eventClass, eventData) {
    this._eventEmiter.next(eventClass, eventData);
  }

  async showLoading() {
    this.logger.debug('showLoading');
  }

  closeForm() {
    this.closeCurrentAlert();
    this.hideSectionModalDialog();
    this.eventSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Clave (params serializados) con la que se inicializó el formulario por
   * última vez. Permite distinguir una navegación real (cambian los params →
   * hay que reconstruir) de una re-emisión de `params` provocada por el
   * reattach de una ruta preservada con `markForReuse()` (mismos params → NO
   * reconstruir). Ver `ngOnInit`.
   */
  private _initedParamsKey: string | null = null;

  override ngOnInit() {
    super.ngOnInit();
    this.hideSectionModalDialog();
    this._route.params.subscribe(params => {
      const paramsKey = JSON.stringify(params);
      const reuseService = (this as any)._routeReuseService;
      const currentPath = this._router.url.split('?')[0].substring(1);
      // Al regresar de un formulario preservado con markForReuse() (reattach),
      // Angular re-emite `params` aunque no cambien. No se debe reconstruir el
      // formulario: cleanStart()/formInit() destruiría el cableado de eventos
      // (handlers de customizeUxEvents y los Subjects de campos/acciones) de la
      // instancia que se está restaurando, dejándola "viva pero sin reaccionar".
      const isReattach = reuseService?.consumeReattaching(currentPath) ?? false;
      const sameParams = this._initedParamsKey !== null && paramsKey === this._initedParamsKey;
      if (isReattach || sameParams) {
        return;
      }
      this._initedParamsKey = paramsKey;
      this.cleanStart();
      /*
        `formInit` es asíncrono y la librería pone `visible = true` —lo que destapa el formulario
        y esconde el esqueleto— después de sus `await`. Marcar la vista justo tras invocarlo era
        marcarla antes de que eso ocurriera, y el formulario se quedaba en el esqueleto para
        siempre: exactamente el fallo mudo que este cableado busca evitar.

        `Promise.resolve` envuelve el retorno sin depender de que la firma lo declare como promesa.
      */
      void Promise.resolve(this.formInit(params)).then(() => this.marcarParaRevision());
    });
    this.subscribeAppEvents(sessionToBeClosed.name, () => {
      this.closeCurrentAlert();
      this.hideSectionModalDialog();
    });
    this.cleanActionServerError();
    this.cleanFieldServerError();
    this.cleanTableServerError();
    // Los errores de servidor cambian `currentError`/`currentAlert`, que la plantilla pinta.
    this.onActionServerError(() => {
      this.displayActionServerError();
      this.marcarParaRevision();
    });
    this.onValidationServerError(() => {
      this.displayValidationServerError();
      this.marcarParaRevision();
    });
    this.onTableServerError(() => {
      this.displayTableServerError();
      this.marcarParaRevision();
    });

    // Transición de estado del formulario: cambia `state`, `enabledSections` y las acciones
    // visibles. Es el único hook global del ciclo; los de acción y validación de campo se
    // registran por código y no sirven de embudo.
    this.onFormChange(() => this.marcarParaRevision());
  }

  ngOnDestroy() {
    this.closeForm();
  }

  getCurrentPosition() {
    return this._geocode.getCurrentPosition();
  }

  disableServerErrorPopup() {
    this._showServerErrorPopup = false;
  }

  enableServerErrorPopup() {
    this._showServerErrorPopup = true;
  }

  /**
   * Aviso de que el estado de los modales cambió, para `<app-form-modals>`.
   *
   * Ese componente es OnPush y recibe un único input, `host`, cuya referencia nunca cambia.
   * El estado de los modales, en cambio, se muta en sitio —`this.sectionModal.visible = true`—,
   * así que sin este aviso su vista no se vuelve a revisar y el diálogo no abría ni cerraba.
   * Mismo patrón que `marcarAlPropagar` para los renderers; ver `GUIA-ONPUSH-FORMULARIOS.md`.
   */
  private readonly _modalesCambiaron = new Subject<void>();
  readonly modalesCambiaron = this._modalesCambiaron.asObservable();

  protected notificarModales(): void {
    this._modalesCambiaron.next();
  }

  showHelpDialog(contentField: string): void {
    this.helpDialog.content = this.getFieldValue(contentField);
    this.helpDialog.visible = true;
    this.notificarModales();
  }

  hideHelpDialog() {
    this.helpDialog.visible = false;
    this.notificarModales();
  }

  /**
   * Sección que `<app-form-modals>` pinta dentro del modal de sección.
   *
   * Existe porque `sections` es `protected` en la clase de formulario de la librería: la
   * plantilla propia sí podía leerlo, pero un componente hijo no. Ver `FormModalsHost`.
   */
  get sectionModalSection(): any {
    return this.sections?.[this.sectionModal.code];
  }

  showSectionModalDialog(code, title, actionsSubSection = null, callback = null, options: any = {}): void {
    const {
      width = DEFAULT_MODAL_WIDTH,
      minHeight = DEFAULT_MODAL_MIN_HEIGHT,
      height,
      maxHeight,
      nzStyle,
      hideFooterClose = false,
      hideClose = false,
      maskClosable = true,
    } = options ?? {};
    this.sectionModal.code = code;
    this.sectionModal.title = title;
    this.sectionModal.visible = true;
    // `height`, `maxHeight` y `nzStyle` se descartaban aquí, así que las plantillas que los
    // leían recibían siempre `undefined` (p. ej. los tres modales de certificados).
    this.sectionModal.properties = {
      width,
      minHeight,
      height,
      maxHeight,
      nzStyle,
      hideFooterClose,
      hideClose,
      maskClosable,
    };
    this.sectionModal.actions = actionsSubSection ? this.getSubSection(code, actionsSubSection) : null;
    if (callback && typeof callback === 'function') {
      this.sectionModal.callback = callback;
    }
    if (actionsSubSection) {
      this.hideSubSection(code, actionsSubSection);
    }
    this.notificarModales();
  }

  hideSectionModalDialog() {
    const callback = this.sectionModal.callback;
    if (callback) {
      callback();
    }
    this.sectionModal.visible = false;
    this.notificarModales();
  }

  showSimpleModalDialog(
    title: string,
    content: string,
    modalOptions: ModalOption[],
    callback: (i: number) => void,
    options: any = {}
  ): void {
    const { width = DEFAULT_MODAL_WIDTH, type = 'text', template } = options ?? {};
    this.simpleModal.title = title;
    this.simpleModal.content = content;
    this.simpleModal.template = template;
    this.simpleModal.type = type;
    this.simpleModal.visible = true;
    this.simpleModal.properties = { width };
    this.simpleModal.options = modalOptions;
    this.simpleModal.callback = i => {
      this.simpleModal.visible = false;
      this.notificarModales();
      callback(i);
    };
    this.notificarModales();
  }

  hideSimpleModalDialog() {
    this.simpleModal.visible = false;
    this.notificarModales();
  }

  async closeCurrentAlert() {
    if (this.currentAlert) {
      await this.currentAlert.dismiss();
      this.currentAlert = null;
    }
  }

  canChangeSection(_fromIndex: number, _toIndex: number) {
    this.logger.debug('canChangeSection');
  }

  async geocodeLocation(geolocationField, addressField) {
    const geoLocation = this.getFieldValue(geolocationField);
    const [lat, lng] = Array.isArray(geoLocation) ? geoLocation : [];
    const { address } = await this._geocode.geocodeLatLng(lat, lng);
    if (addressField) {
      this.setFieldValue(addressField, address);
    }
    return address;
  }

  async geoCodeAddress(addressFields, geolocationField, addressGeocodeField) {
    const fieldsToGeoCode = this.getFieldValue(addressFields);
    let locationAddress = 'Ecuador';
    if (fieldsToGeoCode && Array.isArray(fieldsToGeoCode)) {
      fieldsToGeoCode.forEach(fieldName => {
        const fieldObj = this.getField(fieldName);
        const text = fieldObj?.optionText;
        const value = fieldObj?.value;
        locationAddress += `, ${text ?? value}`;
      });
    }
    const { latitude: lat, longitude: lng, address } = await this._geocode.getLocation(locationAddress);
    if (geolocationField) {
      const currentLocation = this.getFieldValue(geolocationField);
      const [currLat, currLng] = Array.isArray(currentLocation) ? currentLocation : [];
      if (currLat !== lat || currLng !== lng) {
        this.setFieldValue(geolocationField, [lat, lng]);
      }
      if (addressGeocodeField) {
        this.setFieldValue(addressGeocodeField, address);
      }
    }
    return { lat, lng, address };
  }

  setHeaderBadgeCounter(value = 0) {
    this.headerBadgeCounter = value;
  }

  showOverlay(): void {
    this._blockingProcessing = true;
  }

  hideOverlay(): void {
    this._blockingProcessing = false;
  }
}
